import BaseAPIService from './baseAPI';

// Video Scripts Types - Enhanced for MCP Backend
export interface GenerateVideoScriptRequest {
  topic: string;
  platform: 'linkedin' | 'youtube' | 'tiktok' | 'instagram' | 'x';
  duration: 'short' | 'medium' | 'long';
  tone: 'professional' | 'casual' | 'humorous' | 'educational';
}

export interface VideoScriptResponse {
  success: boolean;
  topic: string;
  script: string;
  style: string;
  requested_duration: number;
  estimated_duration: number;
  word_count: number;
  sections: string[];
  visual_cues: string[];
  call_to_action: string;
  model_used: string;
  error?: string;
}

export interface VideoFromPostRequest {
  post_content: string;
  duration: number;
}

export interface VideoFromPostResponse {
  success: boolean;
  original_post_length: number;
  script: string;
  requested_duration: number;
  estimated_duration: number;
  word_count: number;
  sections: string[];
  visual_cues: string[];
  model_used: string;
  error?: string;
}

export interface GenerateTalkingPointsRequest {
  topic: string;
  duration: number;
}

export interface GenerateTalkingPointsResponse {
  success: boolean;
  topic: string;
  talking_points_raw: string;
  talking_points_structured: {
    hook: string;
    main_points: string[];
    statistics: string[];
    conclusion: string;
    call_to_action: string;
  };
  requested_duration: number;
  num_points: number;
  model_used: string;
  error?: string;
}

export interface GenerateBRollRequest {
  script: string;
}

export interface BRollSuggestion {
  timestamp: string;
  description: string;
  purpose: string;
  visual_style: string;
}

export interface GenerateBRollResponse {
  success: boolean;
  script_length: number;
  b_roll_suggestions: BRollSuggestion[];
  model_used: string;
  error?: string;
}

export interface AnalyzeScriptRequest {
  script_text: string;
}

export interface AnalyzeScriptResponse {
  success: boolean;
  script_analysis: string;
  metrics: {
    word_count: number;
    estimated_duration_seconds: number;
    visual_cues_count: number;
    character_count: number;
  };
  model_used: string;
  error?: string;
}

export interface VideoTemplate {
  id: string;
  name: string;
  description: string;
  duration_seconds: number;
  template: string;
  sections: string[];
  visual_cues: string[];
}

export interface GetTemplatesResponse {
  success: boolean;
  platform: string;
  duration: string;
  templates: VideoTemplate[];
  error?: string;
}

class VideoAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  async generateVideoScript(request: GenerateVideoScriptRequest): Promise<VideoScriptResponse> {
    try {
      const response = await this.post<VideoScriptResponse>('/video/generate-video-script', request);
      return response;
    } catch (error) {
      console.error('Error generating video script:', error);
      throw error;
    }
  }

  async generateVideoFromPost(request: VideoFromPostRequest): Promise<VideoFromPostResponse> {
    try {
      const response = await this.post<VideoFromPostResponse>('/video/convert-post-to-script', request);
      return response;
    } catch (error) {
      console.error('Error generating video from post:', error);
      throw error;
    }
  }

  async generateTalkingPoints(request: GenerateTalkingPointsRequest): Promise<GenerateTalkingPointsResponse> {
    try {
      const response = await this.post<GenerateTalkingPointsResponse>('/video/generate-talking-points', request);
      return response;
    } catch (error) {
      console.error('Error generating talking points:', error);
      throw error;
    }
  }

  async generateBRollSuggestions(request: GenerateBRollRequest): Promise<GenerateBRollResponse> {
    try {
      const response = await this.post<GenerateBRollResponse>('/video/generate-b-roll-suggestions', request);
      return response;
    } catch (error) {
      console.error('Error generating B-roll suggestions:', error);
      throw error;
    }
  }

  async analyzeScript(request: AnalyzeScriptRequest): Promise<AnalyzeScriptResponse> {
    try {
      const response = await this.post<AnalyzeScriptResponse>('/video/analyze-script', request);
      return response;
    } catch (error) {
      console.error('Error analyzing script:', error);
      throw error;
    }
  }

  async getVideoTemplates(platform: string, duration: string = 'short'): Promise<GetTemplatesResponse> {
    try {
      const response = await this.get<GetTemplatesResponse>(`/video/templates?platform=${platform}&duration=${duration}`);
      return response;
    } catch (error) {
      console.error('Error getting video templates:', error);
      throw error;
    }
  }

  // Enhanced features
  async getAvailablePlatforms(): Promise<{ data: Record<string, string> }> {
    try {
      const response = await this.get<{ data: Record<string, string> }>('/video/platforms');
      return response;
    } catch (error) {
      console.error('Error getting available platforms:', error);
      throw error;
    }
  }

  async optimizeForPlatform(content: string, platform: string): Promise<{ data: { optimized_content: string; recommendations: string[] } }> {
    try {
      const response = await this.post<{ data: { optimized_content: string; recommendations: string[] } }>('/video/optimize-for-platform', { content, platform });
      return response;
    } catch (error) {
      console.error('Error optimizing for platform:', error);
      throw error;
    }
  }

  // Utility methods for duration conversion
  convertDurationToSeconds(duration: 'short' | 'medium' | 'long'): number {
    const mapping = {
      'short': 60,
      'medium': 120,
      'long': 180
    };
    return mapping[duration] || 60;
  }

  convertSecondsToDuration(seconds: number): 'short' | 'medium' | 'long' {
    if (seconds <= 90) return 'short';
    if (seconds <= 150) return 'medium';
    return 'long';
  }

  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  }
}

export const videoAPI = new VideoAPIService();
export default videoAPI; 